import { useRef } from "react";
import type { CSSProperties } from "react";
import { ETYKIETY_WERDYKTOW, WERDYKTY, type Werdykt } from "@shared/recenzjeTypy.js";

/**
 * Werdykt przy wskazaniu: "Zgadzam się" / "Częściowo" / "Nie zgadzam się". Wyglad i ruch wskaznika z przelacznika
 * segmentowego (SG-1..SG-5, klasy .seg*), ale ZACHOWANIE inne, dlatego osobny komponent:
 *
 * - na starcie NIE MA zaznaczenia (wskaznik niewidoczny): brak klikniecia znaczy "zakladamy zgode", a to nie to samo
 *   co klikniete "Zgadzam sie" (to drugie zapisuje werdykt);
 * - klikniecie zaznaczonego segmentu CZYSCI wybor (wraca do stanu "nic nie ruszone");
 * - to grupa radio (role=radiogroup/radio), nie zakladki: wybor wartosci, a nie przelaczanie widokow.
 *
 * Klawiatura jak w SegmentedControl: tylko jeden segment w kolejnosci Taba (zaznaczony albo pierwszy), strzalki
 * przechodza i zaznaczaja, Home/End skacza na koniec. Odstepstwo opisane w docs/audyt-migracji-ds.md.
 */
export function PrzelacznikWerdyktu({
  etykieta,
  wartosc,
  onZmiana,
}: {
  /** A-5: nazwa grupy dla czytnika ekranu (z kontekstem wskazania, bo takich grup jest wiele). */
  etykieta: string;
  wartosc: Werdykt | null;
  onZmiana: (wartosc: Werdykt | null) => void;
}): React.JSX.Element {
  const korzenRef = useRef<HTMLDivElement>(null);
  const aktywny = wartosc === null ? -1 : WERDYKTY.indexOf(wartosc);

  function wybierz(i: number): void {
    const nowy = WERDYKTY[i];
    if (!nowy) return;
    onZmiana(nowy);
    korzenRef.current?.querySelectorAll<HTMLButtonElement>(".seg__btn")[i]?.focus();
  }

  function naKlawisz(e: React.KeyboardEvent<HTMLButtonElement>, i: number): void {
    const ostatni = WERDYKTY.length - 1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      wybierz(i === ostatni ? 0 : i + 1);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      wybierz(i === 0 ? ostatni : i - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      wybierz(0);
    } else if (e.key === "End") {
      e.preventDefault();
      wybierz(ostatni);
    }
  }

  const zmienne = { "--count": WERDYKTY.length, "--active": Math.max(0, aktywny) } as CSSProperties;

  return (
    <div
      className={`seg rec-werdykt${wartosc === null ? " rec-werdykt--pusty" : ""}`}
      role="radiogroup"
      aria-label={etykieta}
      style={zmienne}
      ref={korzenRef}
    >
      <div className="seg__indicator" aria-hidden="true" />
      {WERDYKTY.map((werdykt, i) => (
        <button
          key={werdykt}
          type="button"
          className="seg__btn"
          role="radio"
          aria-checked={i === aktywny}
          // Roving tabindex: zaznaczony segment, a gdy nic nie zaznaczono, pierwszy.
          tabIndex={i === aktywny || (aktywny === -1 && i === 0) ? 0 : -1}
          onClick={() => (i === aktywny ? onZmiana(null) : wybierz(i))}
          onKeyDown={(e) => naKlawisz(e, i)}
        >
          {ETYKIETY_WERDYKTOW[werdykt]}
        </button>
      ))}
    </div>
  );
}
